import React, { useEffect, useState } from "react";
import { Card, Grid, Button, Tab, Tabs } from "@mui/material";
import { Add as AddIcon } from "components/icons";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { iqmsActions } from "store/iqms-slice";
import { handleAxiosError } from "app/services/app.error.handler";
import {
  fetchAllProcesses,
  fetchAllAudits,
  fetchAllCapa,
  fetchAllRisks,
  deleteProcess,
  deleteAudit,
} from "app/services/iqms.service";
import { ProcessDataGrid } from "./ProcessDataGrid";
import { AuditDataGrid } from "./AuditDataGrid";
import { CapaDataGrid } from "./CapaDataGrid";
import { RiskDataGrid } from "./RiskDataGrid";
import AddProcessDialog from "./AddProcessDialog";
import AddAuditDialog from "./AddAuditDialog";
import AddCapaDialog from "./AddCapaDialog";
import AddRiskDialog from "./AddRiskDialog";
import { ConfirmDialog } from "../ConfirmDialog";

export const IqmsAdminView = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [capaDialogOpen, setCapaDialogOpen] = useState(false);
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  const [editProcess, setEditProcess] = useState(null);
  const [editAudit, setEditAudit] = useState(null);
  const [editCapa, setEditCapa] = useState(null);
  const [editRisk, setEditRisk] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteType, setDeleteType] = useState("");

  const processes = useSelector((state) => state.iqms.processes);
  const audits = useSelector((state) => state.iqms.audits);
  const capas = useSelector((state) => state.iqms.capas);
  const risks = useSelector((state) => state.iqms.risks);

  const loadProcesses = () => {
    setIsLoading(true);
    fetchAllProcesses()
      .then((data) => dispatch(iqmsActions.setProcesses(data)))
      .catch((error) => handleAxiosError(error))
      .finally(() => setIsLoading(false));
  };

  const loadAudits = () => {
    fetchAllAudits()
      .then((data) => dispatch(iqmsActions.setAudits(data)))
      .catch((error) => handleAxiosError(error));
  };

  const loadCapas = () => {
    fetchAllCapa()
      .then((data) => dispatch(iqmsActions.setCapas(data)))
      .catch((error) => handleAxiosError(error));
  };

  const loadRisks = () => {
    fetchAllRisks()
      .then((data) => dispatch(iqmsActions.setRisks(data)))
      .catch((error) => handleAxiosError(error));
  };

  useEffect(() => {
    loadProcesses();
    loadAudits();
    loadCapas();
    loadRisks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = (item, type) => {
    setDeleteItem(item);
    setDeleteType(type);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (deleteType === "process" && deleteItem) {
      await deleteProcess(deleteItem.id);
      loadProcesses();
    } else if (deleteType === "audit" && deleteItem) {
      await deleteAudit(deleteItem.id);
      loadAudits();
    }
    setDeleteItem(null);
    setDeleteType("");
  };

  const openCapas = capas.filter((c) => c.status === "OPEN" || c.status === "IN_PROGRESS").length;
  const activeRisks = risks.filter((r) => r.status !== "CLOSED").length;

  return (
    <MDBox px={5} py={10}>
      <Grid container spacing={10}>
        {/* Stats Cards */}
        <Grid container spacing={10} item xs={12}>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="info">Processes</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{processes.length}</MDTypography>
                <MDTypography variant="caption">Quality Processes</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="success">Audits</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{audits.length}</MDTypography>
                <MDTypography variant="caption">Total Audits</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="warning">CAPA</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{openCapas}</MDTypography>
                <MDTypography variant="caption">Open CAPAs</MDTypography>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <Card sx={{ width: "160px" }}>
              <MDBox p={3} textAlign="center">
                <MDTypography variant="h6" color="error">Risks</MDTypography>
                <MDTypography variant="h3" fontWeight="bold">{activeRisks}</MDTypography>
                <MDTypography variant="caption">Active Risks</MDTypography>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
        {/* Main Content with Tabs */}
        <Grid item xs={12} width="100%">
          <Card>
            <MDBox p={2}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="Processes" />
                <Tab label="Audits" />
                <Tab label="CAPA" />
                <Tab label="Risks" />
              </Tabs>
            </MDBox>
            {tabValue === 0 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button startIcon={<AddIcon />} onClick={() => { setEditProcess(null); setProcessDialogOpen(true); }} variant="contained" color="success">
                    Add Process
                  </Button>
                </MDBox>
                <ProcessDataGrid processes={processes} isLoading={isLoading} onEdit={(p) => { setEditProcess(p); setProcessDialogOpen(true); }} onDelete={(p) => handleDelete(p, "process")} />
              </MDBox>
            )}
            {tabValue === 1 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button startIcon={<AddIcon />} onClick={() => { setEditAudit(null); setAuditDialogOpen(true); }} variant="contained" color="success">
                    Schedule Audit
                  </Button>
                </MDBox>
                <AuditDataGrid audits={audits} isLoading={isLoading} onEdit={(a) => { setEditAudit(a); setAuditDialogOpen(true); }} onDelete={(a) => handleDelete(a, "audit")} />
              </MDBox>
            )}
            {tabValue === 2 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button startIcon={<AddIcon />} onClick={() => { setEditCapa(null); setCapaDialogOpen(true); }} variant="contained" color="success">
                    Create CAPA
                  </Button>
                </MDBox>
                <CapaDataGrid capas={capas} isLoading={isLoading} onEdit={(c) => { setEditCapa(c); setCapaDialogOpen(true); }} onDelete={(c) => handleDelete(c, "capa")} />
              </MDBox>
            )}
            {tabValue === 3 && (
              <MDBox>
                <MDBox px={2} display="flex" justifyContent="flex-end">
                  <Button startIcon={<AddIcon />} onClick={() => { setEditRisk(null); setRiskDialogOpen(true); }} variant="contained" color="success">
                    Add Risk
                  </Button>
                </MDBox>
                <RiskDataGrid risks={risks} isLoading={isLoading} onEdit={(r) => { setEditRisk(r); setRiskDialogOpen(true); }} onDelete={(r) => handleDelete(r, "risk")} />
              </MDBox>
            )}
          </Card>
        </Grid>
      </Grid>

      <AddProcessDialog open={processDialogOpen} setOpen={setProcessDialogOpen} onSuccess={loadProcesses} editObject={editProcess} />
      <AddAuditDialog open={auditDialogOpen} setOpen={setAuditDialogOpen} onSuccess={loadAudits} editObject={editAudit} />
      <AddCapaDialog open={capaDialogOpen} setOpen={setCapaDialogOpen} onSuccess={loadCapas} editObject={editCapa} />
      <AddRiskDialog open={riskDialogOpen} setOpen={setRiskDialogOpen} onSuccess={loadRisks} editObject={editRisk} />
      <ConfirmDialog title={`Delete ${deleteType}?`} open={confirmOpen} setOpen={setConfirmOpen} onConfirm={confirmDelete} yesButtonLabel="Delete" noButtonLabel="Cancel">
        Are you sure you want to delete this {deleteType}?
      </ConfirmDialog>
    </MDBox>
  );
};

